import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  FaSave,
  FaTimes,
  FaArrowLeft,
  FaCog,
  FaCalculator,
  FaMoneyBillWave,
  FaClock,
  FaPercent,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";

const PayRollSettings = () => {
  // Custom styles for compact design
  const compactStyles = {
    formControl: {
      fontSize: '1.1rem',
      padding: '0.75rem 1rem',
      borderRadius: '0.5rem',
      border: '2px solid #e9ecef',
      transition: 'all 0.2s ease-in-out'
    },
    label: {
      fontSize: '1rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#495057'
    },
    sectionHeader: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '1rem'
    },
    card: {
      borderRadius: '0.75rem',
      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
      border: '1px solid rgba(0, 0, 0, 0.125)'
    }
  };
  const [loggedIn, setLoggedIn] = useState(
    localStorage.getItem("token") !== null
  );
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  
  const [salaryTemplateData, setSalaryTemplateData] = useState({
    template_name: "",
    basic_pay: "",
    hra_percentage: "",
    da_percentage: "",
    conveyance: "",
    medical_allowance: "",
    special_allowance: "",
    overtime_enabled: "",
    overtime_rate_type: "",
    overtime_rate_val: "",
    pf_applicable: "",
    pf_percent_employee: "",
    esi_applicable: "",
    esi_percent_employee: "",
    professional_tax_fixed: "",
    tds_default_percent: "",
    prorata_basis: "",
    is_week_off_paid: "",
    is_holiday_paid: "",
    half_day_weight: "",
    late_unit: "",
    late_per_lop: "",
    late_minutes_threshold: "",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSalaryTemplateData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "1" : "0") : value,
    }));
  };

  const resetForm = () => {
    setSalaryTemplateData({
      template_name: "",
      basic_pay: "",
      hra_percentage: "",
      da_percentage: "",
      conveyance: "",
      medical_allowance: "",
      special_allowance: "",
      overtime_enabled: "",
      overtime_rate_type: "",
      overtime_rate_val: "",
      pf_applicable: "",
      pf_percent_employee: "",
      esi_applicable: "",
      esi_percent_employee: "",
      professional_tax_fixed: "",
      tds_default_percent: "",
      prorata_basis: "",
      is_week_off_paid: "",
      is_holiday_paid: "",
      half_day_weight: "",
      late_unit: "",
      late_per_lop: "",
      late_minutes_threshold: "",
    });
    setIsEditMode(false);
    setCurrentTemplateId(null);
    setSelectedTemplate("");
  };

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const FUNCTION_NAME = "getPayrollSalaryTemplates.php";
      const URL =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        FUNCTION_NAME;

      const params = { school_code: SCHOOL_CODE };
      const queryString = new URLSearchParams(params).toString();
      console.log("Full URL: ", `${URL}?${queryString}`);

      const response = await axios.get(`${URL}?${queryString}`);
      console.log("API Response:", response.data);

      if (response.data.api_status === "OK") {
        setTemplates(response.data.data);
      } else {
        console.error("API Error:", response.data.api_remark);
        myToast.notify_error(response.data.api_remark || "Failed to fetch templates");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      myToast.notify_error("Failed to fetch salary templates");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      resetForm();
      return;
    }

    // Find template from the existing templates array
    const template = templates.find(t => t.id === templateId);
    
    if (template) {
      setSelectedTemplate(templateId);
      setIsEditMode(true);
      setCurrentTemplateId(templateId);
      
      // Populate form with template data from the array
      setSalaryTemplateData({
        template_name: template.template_name || "",
        basic_pay: template.basic_pay || "",
        hra_percentage: template.hra_percentage || "40",
        da_percentage: template.da_percentage || "50",
        conveyance: template.conveyance || "1600",
        medical_allowance: template.medical_allowance || "1250",
        special_allowance: template.special_allowance || "0",
        overtime_enabled: template.overtime_enabled || "1",
        overtime_rate_type: template.overtime_rate_type || "Hourly",
        overtime_rate_val: template.overtime_rate_val || "0",
        pf_applicable: template.pf_applicable || "1",
        pf_percent_employee: template.pf_percent_employee || "12",
        esi_applicable: template.esi_applicable || "1",
        esi_percent_employee: template.esi_percent_employee || "1.75",
        professional_tax_fixed: template.professional_tax_fixed || "200",
        tds_default_percent: template.tds_default_percent || "10",
        prorata_basis: template.prorata_basis || "1",
        is_week_off_paid: template.is_week_off_paid || "1",
        is_holiday_paid: template.is_holiday_paid || "1",
        half_day_weight: template.half_day_weight || "0.5",
        late_unit: template.late_unit || "Minutes",
        late_per_lop: template.late_per_lop || "1",
        late_minutes_threshold: template.late_minutes_threshold || "15"
      });
      
      myToast.notify_success(`Template "${template.template_name}" loaded for editing`);
    } else {
      myToast.notify_error("Template not found in the list");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        const SCHOOL_CODE = SessionManager.getSchoolCode();
        const FUNCTION_NAME = "deletePayrollSalaryTemplate.php";
        const URL =
          process.env.REACT_APP_SERVER_NAME +
          process.env.REACT_APP_TARGET_DIR +
          FUNCTION_NAME;

        const params = { 
          school_code: SCHOOL_CODE, 
          template_id: templateId 
        };
        const queryString = new URLSearchParams(params).toString();
        console.log("Delete URL: ", `${URL}?${queryString}`);

        const response = await axios.post(`${URL}?${queryString}`);
        console.log("Delete Response:", response.data);

        if (response.data.api_status === "OK") {
          myToast.notify_success("Template deleted successfully!");
          fetchTemplates(); // Refresh the list
        } else {
          console.error("API Error:", response.data.api_remark);
          myToast.notify_error(response.data.api_remark || "Failed to delete template");
        }
      } catch (error) {
        console.error("Error deleting template:", error);
        myToast.notify_error("Failed to delete template");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!salaryTemplateData.template_name.trim() || !salaryTemplateData.basic_pay) {
      myToast.notify_error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const FUNCTION_NAME = isEditMode 
        ? "updatePayrollSalaryTemplate.php" 
        : "addPayrollSalaryTemplate.php";
      
      const URL =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        FUNCTION_NAME;

      const params = {
        school_code: SCHOOL_CODE,
        template_name: salaryTemplateData.template_name,
        basic_pay: salaryTemplateData.basic_pay,
        hra_percentage: salaryTemplateData.hra_percentage,
        da_percentage: salaryTemplateData.da_percentage,
        conveyance: salaryTemplateData.conveyance,
        medical_allowance: salaryTemplateData.medical_allowance,
        special_allowance: salaryTemplateData.special_allowance,
        overtime_enabled: salaryTemplateData.overtime_enabled,
        overtime_rate_type: salaryTemplateData.overtime_rate_type,
        overtime_rate_val: salaryTemplateData.overtime_rate_val,
        pf_applicable: salaryTemplateData.pf_applicable,
        pf_percent_employee: salaryTemplateData.pf_percent_employee,
        esi_applicable: salaryTemplateData.esi_applicable,
        esi_percent_employee: salaryTemplateData.esi_percent_employee,
        professional_tax_fixed: salaryTemplateData.professional_tax_fixed,
        tds_default_percent: salaryTemplateData.tds_default_percent,
        prorata_basis: salaryTemplateData.prorata_basis,
        is_week_off_paid: salaryTemplateData.is_week_off_paid,
        is_holiday_paid: salaryTemplateData.is_holiday_paid,
        half_day_weight: salaryTemplateData.half_day_weight,
        late_unit: salaryTemplateData.late_unit,
        late_per_lop: salaryTemplateData.late_per_lop,
        late_minutes_threshold: salaryTemplateData.late_minutes_threshold
      };

      // Add template_id for updates
      if (isEditMode) {
        params.template_id = currentTemplateId;
      }

      const queryString = new URLSearchParams(params).toString();
      console.log("Submit URL: ", `${URL}?${queryString}`);

      const response = await axios.post(`${URL}?${queryString}`);
      console.log("Submit Response:", response.data);

      if (response.data.api_status === "OK") {
        myToast.notify_success(isEditMode ? "Template updated successfully!" : "Template added successfully!");
        resetForm();
        fetchTemplates(); // Refresh the list
      } else {
        myToast.notify_error(
          response.data.api_remark || "Operation failed"
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      myToast.notify_error("Failed to save template");
    } finally {
      setLoading(false);
    }
  };



  if (!loggedIn) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <h2>Access Denied</h2>
            <p>You must be logged in to view this page.</p>
            <Link to="/Login" className="btn btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <MyLoader />;
  }

  return (
    <div className="content">
      <div className="content-header">
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <Link to="/AdminMain/PayRoll" aria-label="Payroll home">
                <i className="fa fa-home" aria-hidden="true" />
              </Link>
            </li>
            <li>
              <i className="fa fa-cog" aria-hidden="true" />
              <span>Salary Settings</span>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="bg_page_white_rounded box_shadow animated fadeInUp" style={{ "--mMargin": "30px" }}>
        <div className="payroll-table-container payroll-fade-in mb-4">
          <div className="payroll-table-header">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">
                <FaCog className="me-2" />
                Staff Salary Settings
              </h3>
              <div className="d-flex gap-3">
                <Link to="/AdminMain/PayRoll" className="btn btn-outline-secondary">
                  <FaArrowLeft className="me-2" />
                  Back to Payroll
                </Link>
              </div>
            </div>
          </div>

          {/* Template Edit Form */}
          <div className="card mb-3">
            <div className="card-body p-3">
              {/* Connection Indicator */}
              {selectedTemplate && (
                <div className="alert alert-success mb-3 py-2">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-link text-success me-2 fa-lg" />
                    <div>
                      <h6 className="mb-1 text-success fs-6">
                        <i className="fa fa-arrow-down me-2" />
                        Template Data Connected
                      </h6>
                      <p className="mb-0 text-muted small">
                        The form below is now populated with data from "<strong>{templates.find(t => t.id === selectedTemplate)?.template_name}</strong>"
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* No Template Selected Indicator */}
              {!selectedTemplate && (
                <div className="alert alert-info mb-3 py-2">
                  <div className="d-flex align-items-center">
                    <i className="fa fa-info-circle me-2 fa-lg" />
                    <div>
                      <h6 className="mb-1 text-info fs-6">
                        <i className="fa fa-arrow-up me-2" />
                        Select a Template to Edit
                      </h6>
                      <p className="mb-0 text-muted small">
                        Choose a template from the dropdown below to load it into this form for editing.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Template Selection Dropdown */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="border border-danger rounded p-3 bg-light">
                    <h5 className="text-danger mb-2 fs-5">
                      <FaCog className="me-2" />
                      0. Template Selection
                    </h5>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-2">
                          <label className="form-label fw-bold fs-6">
                            <strong>Select Template for Editing:</strong>
                          </label>
                          <select
                            className="form-select form-select-lg"
                            value={selectedTemplate}
                            onChange={(e) => handleTemplateSelect(e.target.value)}
                          >
                            <option value="">-- Select Template --</option>
                            {templates.map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.template_name}
                              </option>
                            ))}
                          </select>
                          {selectedTemplate && (
                            <div className="mt-2">
                              <small className="text-success fw-bold">
                                <i className="fa fa-check-circle me-1" />
                                Template selected for editing
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6 d-flex align-items-end">
                        <button
                          className="btn btn-outline-danger btn-lg me-2"
                          onClick={fetchTemplates}
                          disabled={templatesLoading}
                        >
                          {templatesLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-refresh me-2" />
                              Refresh Templates
                            </>
                          )}
                        </button>
                        {isEditMode && (
                          <button
                            className="btn btn-outline-warning btn-lg ms-2"
                            onClick={resetForm}
                            title="Clear Form"
                          >
                            <FaTimes />
                            Clear Form
                          </button>
                        )}
                      </div>
                    </div>

                    {!templatesLoading && templates.length === 0 && (
                      <div className="text-center py-3">
                        <i className="fa fa-info-circle fa-2x text-muted mb-2" />
                        <p className="text-muted mb-0">No salary templates found. Create your first template below.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Template Information */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="border border-primary rounded p-3 bg-light">
                      <h5 className="text-primary mb-2 fs-5">
                        <FaCalculator className="me-2" />
                        {isEditMode ? `Editing: ${salaryTemplateData.template_name}` : 'Template Information'}
                      </h5>
                      {isEditMode && (
                        <div className="alert alert-info py-2 mb-2 small">
                          <i className="fa fa-info-circle me-2" />
                          <strong>Template ID:</strong> {currentTemplateId} | 
                          <strong>Last Modified:</strong> {new Date().toLocaleDateString()}
                        </div>
                      )}
                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">
                              Template Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-lg"
                              name="template_name"
                              value={salaryTemplateData.template_name}
                              onChange={handleInputChange}
                              placeholder="Enter Template Name"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">
                              Basic Pay <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="basic_pay"
                              value={salaryTemplateData.basic_pay}
                              onChange={handleInputChange}
                              placeholder="Enter Basic Pay"
                              min="0"
                              step="0.01"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Prorata Basis</label>
                            <select
                              className="form-select form-select-lg"
                              name="prorata_basis"
                              value={salaryTemplateData.prorata_basis}
                              onChange={handleInputChange}
                            >
                              <option value="working_days">Working Days</option>
                              <option value="calendar_days">Calendar Days</option>
                              <option value="month_30_days">Month (30 Days)</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 1. Allowances */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="border border-success rounded p-3 bg-light">
                      <h5 className="text-success mb-2 fs-5">
                        <FaMoneyBillWave className="me-2" />
                        1. Allowances
                      </h5>
                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-bold fs-6 mb-0">HRA Percentage</label>
                              {salaryTemplateData.basic_pay && salaryTemplateData.hra_percentage && (
                                <small className="text-success fw-bold">
                                  ₹{((parseFloat(salaryTemplateData.basic_pay) * parseFloat(salaryTemplateData.hra_percentage)) / 100).toFixed(2)}
                                </small>
                              )}
                            </div>
                            <div className="input-group input-group-lg">
                              <input
                                type="number"
                                className="form-control"
                                name="hra_percentage"
                                value={salaryTemplateData.hra_percentage}
                                onChange={handleInputChange}
                                placeholder="HRA %"
                                min="0"
                                max="100"
                                step="0.01"
                              />
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-bold fs-6 mb-0">DA Percentage</label>
                              {salaryTemplateData.basic_pay && salaryTemplateData.da_percentage && (
                                <small className="text-success fw-bold">
                                  ₹{((parseFloat(salaryTemplateData.basic_pay) * parseFloat(salaryTemplateData.da_percentage)) / 100).toFixed(2)}
                                </small>
                              )}
                            </div>
                            <div className="input-group input-group-lg">
                              <input
                                type="number"
                                className="form-control"
                                name="da_percentage"
                                value={salaryTemplateData.da_percentage}
                                onChange={handleInputChange}
                                placeholder="DA %"
                                min="0"
                                max="100"
                                step="0.01"
                              />
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Conveyance</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="conveyance"
                              value={salaryTemplateData.conveyance}
                              onChange={handleInputChange}
                              placeholder="Conveyance Amount"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Medical Allowance</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="medical_allowance"
                              value={salaryTemplateData.medical_allowance}
                              onChange={handleInputChange}
                              placeholder="Medical Allowance"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Special Allowance</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="special_allowance"
                              value={salaryTemplateData.special_allowance}
                              onChange={handleInputChange}
                              placeholder="Special Allowance"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Professional Tax</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="professional_tax_fixed"
                              value={salaryTemplateData.professional_tax_fixed}
                              onChange={handleInputChange}
                              placeholder="Professional Tax"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Deductions */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="border border-info rounded p-3 bg-light">
                      <h5 className="text-info mb-2 fs-5">
                       
                        2. Statutory Deductions(PF & ESI) <FaPercent className="me-2" />
                      </h5>
                      <div className="row">
                        <div className="col-md-3">
                          <div className="mb-4">
                            <label className="form-label fw-bold fs-6">PF Applicable</label>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="pf_applicable"
                                id="pfApplicable"
                                checked={salaryTemplateData.pf_applicable === "1"}
                                onChange={handleInputChange}
                                style={{ width: '1.5rem', height: '1.5rem' }}
                              />
                              <label className="form-check-label fw-bold fs-6 ms-2" htmlFor="pfApplicable">
                                Enable PF deduction
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-bold fs-6 mb-0">PF Employee %</label>
                              {salaryTemplateData.basic_pay && salaryTemplateData.pf_percent_employee && salaryTemplateData.pf_applicable === "1" && (
                                <small className="text-danger fw-bold">
                                  ₹{((parseFloat(salaryTemplateData.basic_pay) * parseFloat(salaryTemplateData.pf_percent_employee)) / 100).toFixed(2)}
                                </small>
                              )}
                            </div>
                            <div className="input-group input-group-lg">
                              <input
                                type="number"
                                className="form-control"
                                name="pf_percent_employee"
                                value={salaryTemplateData.pf_percent_employee}
                                onChange={handleInputChange}
                                placeholder="PF %"
                                min="0"
                                max="100"
                                step="0.01"
                              />
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-4">
                            <label className="form-label fw-bold fs-6">ESI Applicable</label>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="esi_applicable"
                                id="esiApplicable"
                                checked={salaryTemplateData.esi_applicable === "1"}
                                onChange={handleInputChange}
                                style={{ width: '1.5rem', height: '1.5rem' }}
                              />
                              <label className="form-check-label fw-bold fs-6 ms-2" htmlFor="esiApplicable">
                                Enable ESI deduction
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-bold fs-6 mb-0">ESI Employee %</label>
                              {salaryTemplateData.basic_pay && salaryTemplateData.esi_percent_employee && salaryTemplateData.esi_applicable === "1" && (
                                <small className="text-danger fw-bold">
                                  ₹{((parseFloat(salaryTemplateData.basic_pay) * parseFloat(salaryTemplateData.esi_percent_employee)) / 100).toFixed(2)}
                                </small>
                              )}
                            </div>
                            <div className="input-group input-group-lg">
                              <input
                                type="number"
                                className="form-control"
                                name="esi_percent_employee"
                                value={salaryTemplateData.esi_percent_employee}
                                onChange={handleInputChange}
                                placeholder="ESI %"
                                min="0"
                                max="100"
                                step="0.01"
                              />
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Attendance Settings */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="border border-dark rounded p-3 bg-light">
                      <h5 className="text-dark mb-2 fs-5">
                        <FaClock className="me-2" />
                        3. Attendance & Late Settings
                      </h5>
                      <div className="row">
                        <div className="col-md-3">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Half Day Weight</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="half_day_weight"
                              value={salaryTemplateData.half_day_weight}
                              onChange={handleInputChange}
                              placeholder="Half Day Weight"
                              min="0"
                              max="1"
                              step="0.1"
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Late Unit</label>
                            <select
                              className="form-select form-select-lg"
                              name="late_unit"
                              value={salaryTemplateData.late_unit}
                              onChange={handleInputChange}
                            >
                              <option value="Minutes">Minutes</option>
                              <option value="Hours">Hours</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Late per LOP</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="late_per_lop"
                              value={salaryTemplateData.late_per_lop}
                              onChange={handleInputChange}
                              placeholder="Late per LOP"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Late Threshold (Minutes)</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="late_minutes_threshold"
                              value={salaryTemplateData.late_minutes_threshold}
                              onChange={handleInputChange}
                              placeholder="Late Threshold"
                              min="0"
                              step="1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. Overtime Settings */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="border border-warning rounded p-3 bg-light">
                      <h5 className="text-warning mb-2 fs-5">
                        <FaClock className="me-2" />
                        4. Overtime Settings
                      </h5>
                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-4">
                            <label className="form-label fw-bold fs-6">Overtime Enabled</label>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="overtime_enabled"
                                id="overtimeEnabled"
                                checked={salaryTemplateData.overtime_enabled === "1"}
                                onChange={handleInputChange}
                                style={{ width: '1.5rem', height: '1.5rem' }}
                              />
                              <label className="form-check-label fw-bold fs-6 ms-2" htmlFor="overtimeEnabled">
                                Enable overtime calculation
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Overtime Rate Type</label>
                            <select
                              className="form-select form-select-lg"
                              name="overtime_rate_type"
                              value={salaryTemplateData.overtime_rate_type}
                              onChange={handleInputChange}
                            >
                              <option value="Hourly">Hourly</option>
                              <option value="Daily">Daily</option>
                              <option value="Percentage">Percentage</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">Overtime Rate Value</label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              name="overtime_rate_val"
                              value={salaryTemplateData.overtime_rate_val}
                              onChange={handleInputChange}
                              placeholder="Overtime Rate"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 5. Additional Settings */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="border border-secondary rounded p-3 bg-light">
                      <h5 className="text-secondary mb-2 fs-5">
                        <FaCog className="me-2" />
                        5. Additional Settings
                      </h5>
                      <div className="row">
                        <div className="col-md-3">
                          <div className="mb-2">
                            <label className="form-label fw-bold fs-6">TDS Default %</label>
                            <div className="input-group input-group-lg">
                              <input
                                type="number"
                                className="form-control"
                                name="tds_default_percent"
                                value={salaryTemplateData.tds_default_percent}
                                onChange={handleInputChange}
                                placeholder="TDS %"
                                min="0"
                                max="100"
                                step="0.01"
                              />
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="mb-4">
                            <label className="form-label fw-bold fs-6">Week Off Paid</label>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="is_week_off_paid"
                                id="weekOffPaid"
                                checked={salaryTemplateData.is_week_off_paid === "1"}
                                onChange={handleInputChange}
                                style={{ width: '1.5rem', height: '1.5rem' }}
                              />
                              <label className="form-check-label fw-bold fs-6 ms-2" htmlFor="weekOffPaid">
                                Week off is paid
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="mb-4">
                            <label className="form-label fw-bold fs-6">Holiday Paid</label>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="is_holiday_paid"
                                id="holidayPaid"
                                checked={salaryTemplateData.is_holiday_paid === "1"}
                                onChange={handleInputChange}
                                style={{ width: '1.5rem', height: '1.5rem' }}
                              />
                              <label className="form-check-label fw-bold fs-6 ms-2" htmlFor="holidayPaid">
                                Holidays are paid
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-2 justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-lg"
                    onClick={resetForm}
                  >
                    <FaTimes className="me-2" />
                    Reset Form
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading || !selectedTemplate}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        {isEditMode ? "Updating..." : "Saving..."}
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {isEditMode ? `Update "${salaryTemplateData.template_name}"` : "Select a template to edit"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>



          {/* Template Details Table */}
          <div className="card mb-3">
            <div className="card-body p-3">
              <h5 className="text-primary mb-2 fs-5">
                <i className="fa fa-table me-2" />
                Template Details
              </h5>
              {templates.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Template Name</th>
                        <th>Basic Pay</th>
                        <th>HRA %</th>
                        <th>DA %</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((template) => (
                        <tr key={template.id}>
                          <td>
                            <strong>{template.template_name}</strong>
                          </td>
                          <td>₹{template.basic_pay}</td>
                          <td>{template.hra_percentage}%</td>
                          <td>{template.da_percentage}%</td>
                          <td>
                            <div className="btn-group" role="group">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleTemplateSelect(template.id)}
                                title="Edit Template"
                              >
                                <i className="fa fa-edit" />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteTemplate(template.id)}
                                title="Delete Template"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Template Preview Cards */}
          <div className="card mb-3">
            <div className="card-body p-3">
              <h6 className="text-muted mb-2 fs-6">
                <i className="fa fa-eye me-2" />
                Template Preview Examples
              </h6>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="card template-card h-100 border-primary">
                    <div className="card-body text-center">
                      <div className="template-icon mb-3">
                        <i className="fa fa-calculator fa-2x text-primary" />
                      </div>
                      <h6 className="card-title text-primary">Standard Template</h6>
                      <p className="card-text text-muted small">
                        Basic salary structure with standard allowances
                      </p>
                      <div className="template-details">
                        <small className="text-muted">
                          <strong>Basic Pay:</strong> ₹25,000<br />
                          <strong>HRA:</strong> 40%<br />
                          <strong>DA:</strong> 50%<br />
                          <strong>PF:</strong> 12%
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="card template-card h-100 border-success">
                    <div className="card-body text-center">
                      <div className="template-icon mb-3">
                        <i className="fa fa-star fa-2x text-success" />
                      </div>
                      <h6 className="card-title text-success">Executive Template</h6>
                      <p className="card-text text-muted small">
                        Premium salary structure for executives
                      </p>
                      <div className="template-details">
                        <small className="text-muted">
                          <strong>Basic Pay:</strong> ₹50,000<br />
                          <strong>HRA:</strong> 50%<br />
                          <strong>DA:</strong> 60%<br />
                          <strong>PF:</strong> 12%
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="card template-card h-100 border-warning">
                    <div className="card-body text-center">
                      <div className="template-icon mb-3">
                        <i className="fa fa-plus-circle fa-2x text-warning" />
                      </div>
                      <h6 className="card-title text-warning">Custom Template</h6>
                      <p className="card-text text-muted small">
                        Design your own salary structure
                      </p>
                      <div className="template-details">
                        <small className="text-muted">
                          <strong>Flexible:</strong> All components<br />
                          <strong>Customizable:</strong> Multiple options<br />
                          <strong>Scalable:</strong> Easy to modify
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayRollSettings;


